import { computed, watch, inject, hasInjectionContext } from 'vue'
import { matrixEnvironmentKey } from './matrixEnvironment'
import type { ComputedRef } from 'vue'

import { useProfileStore } from '@/modules/profile/store/profileStore'
import { getCurrentLevel } from '@/modules/matrix/levels'

/**
 * Minimal structural shape of the Vuex `listOfTypes` state used to derive the
 * current opened level. Declared locally so this bridge has no hard dependency
 * on the ported matrix interfaces (which stay internal to the module).
 */
interface SessionListOfTypes {
  opened?: string[] | null
}

export interface MatrixSession {
  /** Current user id from the shell profile store (null when unknown). */
  userId: ComputedRef<number | null>
  /** Whether the current user is a moderator. */
  isModer: ComputedRef<boolean>
  /**
   * Resolve the current user id, awaiting the profile load if needed.
   *
   * The shell loads the profile in the background (AppLayout), so a deep-link
   * into the matrix can run before `userId` is populated. API calls that gate
   * on the owner id (e.g. `get-clones`) MUST use this instead of `userId.value`
   * to avoid sending `0` and getting a 403.
   */
  ensureUserId: () => Promise<number | null>
  /** Current opened level derived from the Vuex `listOfTypes.opened` list. */
  getCurrentLevel: (listOfTypes?: SessionListOfTypes | null) => number
}

/**
 * Session bridge — the only sanctioned source of user identity for the matrix
 * module. Replaces the legacy global user-data object (id / level / moder) by
 * reading the shell Pinia profile store and the local Vuex `listOfTypes` state.
 */
export function useMatrixSession(): MatrixSession {
  const environment = hasInjectionContext() ? inject(matrixEnvironmentKey, null) : null
  if (environment) return environment.session
  const profileStore = useProfileStore()

  const userId = computed<number | null>(() => profileStore.profile?.id ?? null)
  const isModer = computed<boolean>(() => profileStore.profile?.is_moder === true)

  // Resolve once the in-flight (or freshly started) profile load settles.
  const waitForProfileSettled = (): Promise<void> => {
    if (!profileStore.loading) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const stop = watch(
        () => profileStore.loading,
        (loading) => {
          if (!loading) {
            stop()
            resolve()
          }
        },
      )
    })
  }

  const ensureUserId = async (): Promise<number | null> => {
    if (profileStore.profile?.id) {
      return profileStore.profile.id
    }

    if (profileStore.loading) {
      // Another caller (AppLayout bootstrap) is already fetching — wait for it.
      await waitForProfileSettled()
    } else {
      await profileStore.fetchProfile()
    }

    return profileStore.profile?.id ?? null
  }

  return { userId, isModer, ensureUserId, getCurrentLevel }
}
